import * as THREE from 'three';
import { createAtmosphere } from './atmosphere';
import { isMobileDevice } from '../textures';

/**
 * Globe radius in world units. All other geometry in the scene is sized
 * relative to this — markers, border offsets, atmosphere scale. Keeping it
 * at 1 makes the math elsewhere (latLonToVec3, etc.) clean.
 */
export const GLOBE_RADIUS = 1;

/** Canonical camera distance at which the full globe fits comfortably in view. */
export const CAMERA_INITIAL_Z = 3.2;

/** Vertical field of view in degrees. */
export const CAMERA_FOV = 40;

/**
 * Closest / furthest the camera can zoom. MIN is the camera's distance from
 * the globe's center (radius = 1) — anything ≤ 1.0 puts the camera inside
 * the sphere, and getting too close (< ~1.15) starts clipping into the
 * atmosphere shader's outer hull. 1.2 leaves the camera 0.2 world-units
 * above the surface and feels close without artifacts.
 */
export const CAMERA_ZOOM_MIN = 1.2;
export const CAMERA_ZOOM_MAX = 8;

export interface GlobeScene {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  globeGroup: THREE.Group;
  /**
   * Opaque dark sphere just inside the texture sphere. Always visible;
   * shown through when the texture layer fades out (outline mode), so we
   * see a dark globe instead of a hole into the page background.
   */
  base: THREE.Mesh;
  /** Texture overlay sphere. Transparent; opacity drives photo↔outline transitions. */
  surface: THREE.Mesh;
  atmosphere: THREE.Mesh;
}

export interface CreateGlobeOptions {
  /** Width/height of the container at creation time. Used for camera aspect. */
  width: number;
  height: number;
  /** Base sphere color in outline / no-texture mode. Defaults to a dim navy. */
  surfaceColor?: THREE.ColorRepresentation;
}

/**
 * Build the initial scene, camera, and globe group. Returns the mutable
 * handles the engine will use to update state each frame. Caller owns the
 * lifecycle — call `disposeGlobe` to free GPU resources.
 */
export function createGlobeScene(options: CreateGlobeOptions): GlobeScene {
  const { width, height, surfaceColor = 0x1a1f28 } = options;

  const scene = new THREE.Scene();

  const camera = new THREE.PerspectiveCamera(
    CAMERA_FOV,
    width / height,
    0.01, // near — we're always outside the globe
    100, // far — plenty for our scene
  );
  camera.position.set(0, 0, CAMERA_INITIAL_Z);

  // Globe group — we rotate this, not the camera, so lat/lon math stays stable.
  const globeGroup = new THREE.Group();
  globeGroup.name = 'globe';

  // Sphere segment count — 96² gives a perceptually-perfect silhouette
  // at the closest zoom on desktop, but mobile renders at lower DPR with
  // smaller screens, so the silhouette difference at 64² is invisible
  // and the triangle count drops by ~56%. Two spheres (base + surface)
  // both benefit, plus the texture is sampled across fewer fragments.
  const sphereSegments = isMobileDevice() ? 64 : 96;

  // Opaque dark base — ensures outline mode (texture faded out) still shows
  // a globe shape rather than a transparent hole through to space. Sits
  // just inside the texture sphere; never moves; never fades.
  const baseGeom = new THREE.SphereGeometry(GLOBE_RADIUS * 0.999, sphereSegments, sphereSegments);
  const baseMat = new THREE.MeshBasicMaterial({ color: surfaceColor });
  const base = new THREE.Mesh(baseGeom, baseMat);
  base.name = 'globe.base';
  globeGroup.add(base);

  // Texture overlay. BasicMaterial keeps the texture unlit — the NASA Blue
  // Marble image already bakes in continent/ocean shading. Lighting gets
  // added when we introduce day/night terminator effects later.
  // transparent: true so the texture layer can fade in/out during composition
  // transitions (photo ↔ outline mode). Fully opaque pixels still render
  // correctly through the transparent pipeline.
  const surfaceGeom = new THREE.SphereGeometry(GLOBE_RADIUS, sphereSegments, sphereSegments);
  const surfaceMat = new THREE.MeshBasicMaterial({
    color: surfaceColor,
    transparent: true,
    opacity: 1,
  });
  const surface = new THREE.Mesh(surfaceGeom, surfaceMat);
  surface.name = 'globe.surface';
  globeGroup.add(surface);

  const atmosphere = createAtmosphere({ globeRadius: GLOBE_RADIUS });
  globeGroup.add(atmosphere);

  scene.add(globeGroup);

  return { scene, camera, globeGroup, base, surface, atmosphere };
}

/**
 * Swap in a new surface texture (e.g. after progressive tier upgrade).
 * Disposes the previous texture if there was one. Safe to call repeatedly
 * while textures stream in.
 */
export function applyGlobeTexture(surface: THREE.Mesh, texture: THREE.Texture): void {
  const mat = surface.material as THREE.MeshBasicMaterial;
  const previous = mat.map;
  mat.map = texture;
  mat.color.set(0xffffff); // let the texture show through instead of tinting
  mat.needsUpdate = true;
  previous?.dispose();
}

/** Free GPU resources owned by the globe scene. Call on unmount. */
export function disposeGlobe(handle: GlobeScene): void {
  const surfaceMat = handle.surface.material as THREE.MeshBasicMaterial;
  surfaceMat.map?.dispose();
  handle.surface.geometry.dispose();
  surfaceMat.dispose();
  handle.base.geometry.dispose();
  (handle.base.material as THREE.Material).dispose();
  handle.atmosphere.geometry.dispose();
  (handle.atmosphere.material as THREE.Material).dispose();
}
